// pkg79 — is_hq added to DEFAULTS: the public firm-info payload carries it (backend pkg79) and the app renders the standalone HQ shell at hq.lawmighty.com when it is TRUE (D-79-r2) — delivered 2026-07-13
// pkg32 — Emergency Alert banner: the public firm-info payload now includes an
// `alert` field (the firm's active banner, or null). It flows through the
// spread below into context automatically; we only add it to DEFAULTS so
// `firm.alert` is always defined for the banner component.
//
// Provides firm settings (name, phone, address, contact email, etc.) to the
// whole app. Loads once on mount via the public /api/public/firm-info
// endpoint (no auth required), so login screens can show the firm name
// before the user signs in. Admin pages that update firm settings can call
// `refresh()` to pull the latest values into context.

import React, {
  createContext, useContext, useEffect, useState, useCallback,
} from 'react';
import { publicApi } from '../utils/api';
import { firmTimeZoneFrom, setActiveFirmTimeZone } from '../utils/firmDate';

// Sensible defaults so components can render before the fetch completes
// (and so a fresh install with no row still produces a working UI).
const DEFAULTS = {
  firm_name: 'Firm Portal',
  phone: '',
  client_contact_email: '',
  website: '',
  business_street: '',
  business_city: '',
  business_state: '',
  business_zip: '',
  // pkg32 — the firm's active emergency-alert banner, or null when off.
  alert: null,
  // pkg79 — TRUE only at hq.lawmighty.com (the seeded HQ operator firm).
  is_hq: false,
};

const FirmSettingsContext = createContext({
  firm: DEFAULTS,
  loading: true,
  refresh: () => {},
});

export function useFirmSettings() {
  return useContext(FirmSettingsContext);
}

// Convenience: returns the firm name with a sensible default.
export function useFirmName() {
  const { firm } = useFirmSettings();
  return firm.firm_name || DEFAULTS.firm_name;
}

export function FirmSettingsProvider({ children }) {
  const [firm, setFirm]       = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  setActiveFirmTimeZone(firmTimeZoneFrom(firm));

  const refresh = useCallback(async () => {
    try {
      const data = await publicApi.getFirmInfo();
      setFirm({ ...DEFAULTS, ...data });
    } catch (err) {
      // Keep defaults on error — the app still renders.
      console.warn('Could not load firm settings:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Keep document title in sync with firm name. Pages can still override
  // their own title; this is the default that shows on every page load.
  useEffect(() => {
    if (firm.firm_name) {
      document.title = firm.firm_name;
    }
  }, [firm.firm_name]);

  return (
    <FirmSettingsContext.Provider value={{ firm, loading, refresh }}>
      {children}
    </FirmSettingsContext.Provider>
  );
}
